import React, { useEffect, useState } from 'react';
import { getStudentFeeStructure } from '../../../services/feeService';
import { Printer, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const FeeStructure = ({ student }) => {
  const [institution, setInstitution] = useState(null);
  const [feeData, setFeeData] = useState({
    baseTuition: 0,
    m1: 0,
    m2: 0,
    m3: 0,
    billingMode: 'program_based',
    billingModeLabel: 'Programme-based tuition',
    unitCount: 0,
    summativeExamBookings: [],
  });

  useEffect(() => {
    let isCurrent = true;
    const loadTuition = async () => {
      try {
        const result = await getStudentFeeStructure(student);
        if (!isCurrent) return;
        setInstitution(result.institution);
        setFeeData(result);
      } catch (error) {
        console.error('Unable to load the fee structure tuition:', error);
      }
    };
    loadTuition();
    return () => { isCurrent = false; };
  }, [student?.profile_id, student?.id, student?.program_id, student?.program?.id, student?.academic_level]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const element = document.getElementById('fee-structure-document');
      if (!element) return;

      // Add a slight delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2, 
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      
      pdf.save(`Fee_Structure.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert(`PDF Generation Error: ${err.message}. Please check console.`);
    }
  };

  const statutoryFees = institution?.statutory_fees_config || [];

  const formatMoney = (amount) => {
    return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getColTotal = (moduleNum) => {
    let total = moduleNum === 1 ? feeData.m1 : moduleNum === 2 ? feeData.m2 : feeData.m3;
    statutoryFees.forEach(fee => {
      if ((fee.modules || []).includes(moduleNum)) {
        total += Number(fee.amount || 0);
      }
    });
    (feeData.summativeExamBookings || []).forEach(booking => {
      const bookingModule = Number(booking.module_number || 1);
      if (bookingModule === moduleNum) total += Number(booking.fee_amount || 0);
    });
    return total;
  };

  const summativeExamBookings = feeData.summativeExamBookings || [];

  const getTotalPA = () => {
    return getColTotal(1) + getColTotal(2) + getColTotal(3);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Bar */}
      <div className="flex justify-end gap-3 mb-6 print:hidden">
        <button 
          onClick={handlePrint}
          className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors shadow-sm"
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button 
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 font-medium text-sm transition-colors shadow-sm"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* Document Paper */}
      <div id="fee-structure-document" className="bg-white border shadow-sm p-10 md:p-14 print:p-0 print:border-none print:shadow-none bg-white min-h-[1056px] relative flex flex-col">
        
        {/* Header Section */}
        <div className="text-center border-b-4 border-black pb-4 mb-8">
          <div className="flex justify-center mb-4">
            {institution?.logo_url ? (
              <img src={institution.logo_url} alt="Institution Logo" className="h-24 object-contain" />
            ) : (
              <div className="h-24 w-24 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-2xl">
                LOGO
              </div>
            )}
          </div>
          <h1 className="text-2xl font-extrabold uppercase tracking-wide text-black mb-1">
            {institution?.name || 'UNIVERSITY OF EMBU'}
          </h1>
          <h2 className="text-xl font-bold uppercase tracking-wider text-black">
            TVET INSTITUTE
          </h2>
          <div className="mt-4 border-t-2 border-black pt-2 w-full mx-auto">
            <h3 className="text-lg font-bold uppercase tracking-widest text-black">
              FEE STRUCTURE
            </h3>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 text-black text-[15px]">
          <h4 className="font-bold underline uppercase mb-3">
            FEE STRUCTURE FOR {student?.program?.name || 'CRAFT CERTIFICATE'}
          </h4>
          <p className="mb-3 text-xs text-slate-700 print:text-black">
            Tuition basis: <strong>{feeData.billingModeLabel}</strong>
            {feeData.billingMode === 'unit_based' && feeData.unitCount > 0 ? ` · ${feeData.unitCount} programme units` : ''}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-sm">
              <thead>
                <tr className="border border-black">
                  <th className="border-r border-black p-2 text-left w-[40%]"></th>
                  <th className="border-r border-black p-2 text-center font-bold">KSH</th>
                  <th className="border-r border-black p-2 text-center font-bold">KSH</th>
                  <th className="border-r border-black p-2 text-center font-bold">KSH</th>
                  <th className="p-2 text-center font-bold">KSH</th>
                </tr>
                <tr className="border-b-2 border-black">
                  <th className="border-r border-black p-2 text-left"></th>
                  <th className="border-r border-black p-2 text-center font-bold">MODULE 1</th>
                  <th className="border-r border-black p-2 text-center font-bold">MODULE 2</th>
                  <th className="border-r border-black p-2 text-center font-bold">MODULE 3</th>
                  <th className="p-2 text-center font-bold">TOTAL (P.A)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 font-bold">TUITION FEES</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(feeData.m1)}</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(feeData.m2)}</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(feeData.m3)}</td>
                  <td className="p-2 text-right">{formatMoney(feeData.baseTuition)}</td>
                </tr>
                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 font-bold italic" colSpan={5}>OTHERS</td>
                </tr>
                
                {statutoryFees.map((fee, idx) => {
                  const m1 = (fee.modules || []).includes(1) ? Number(fee.amount || 0) : 0;
                  const m2 = (fee.modules || []).includes(2) ? Number(fee.amount || 0) : 0;
                  const m3 = (fee.modules || []).includes(3) ? Number(fee.amount || 0) : 0;
                  const rowTotal = m1 + m2 + m3;
                  
                  return (
                    <tr key={idx} className="border-b border-black">
                      <td className="border-r border-black p-2 pl-4">{fee.name}</td>
                      <td className="border-r border-black p-2 text-right">{m1 > 0 ? formatMoney(m1) : ''}</td>
                      <td className="border-r border-black p-2 text-right">{m2 > 0 ? formatMoney(m2) : ''}</td>
                      <td className="border-r border-black p-2 text-right">{m3 > 0 ? formatMoney(m3) : ''}</td>
                      <td className="p-2 text-right">{formatMoney(rowTotal)}</td>
                    </tr>
                  );
                })}

                {summativeExamBookings.length > 0 && (
                  <>
                    <tr className="border-b border-black">
                      <td className="border-r border-black p-2 font-bold italic" colSpan={5}>SUMMATIVE EXAMINATION FEES</td>
                    </tr>
                    {summativeExamBookings.map((booking, idx) => {
                      const moduleNumber = Number(booking.module_number || 1);
                      const fee = Number(booking.fee_amount || 0);
                      const unitLabel = booking.unit?.code || booking.unit_id || 'Registered unit';
                      const unitName = booking.unit?.name ? ` · ${booking.unit.name}` : '';
                      const typeLabel = String(booking.unit_type || 'core').replace(/^./, letter => letter.toUpperCase());
                      return (
                        <tr key={booking.id || `${unitLabel}-${idx}`} className="border-b border-black">
                          <td className="border-r border-black p-2 pl-4">Summative examination · {unitLabel}{unitName} ({typeLabel})</td>
                          <td className="border-r border-black p-2 text-right">{moduleNumber === 1 ? formatMoney(fee) : ''}</td>
                          <td className="border-r border-black p-2 text-right">{moduleNumber === 2 ? formatMoney(fee) : ''}</td>
                          <td className="border-r border-black p-2 text-right">{moduleNumber === 3 ? formatMoney(fee) : ''}</td>
                          <td className="p-2 text-right">{formatMoney(fee)}</td>
                        </tr>
                      );
                    })}
                  </>
                )}

                <tr className="border-2 border-black font-bold bg-slate-50 print:bg-transparent">
                  <td className="border-r border-black p-2">TOTAL</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(getColTotal(1))}</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(getColTotal(2))}</td>
                  <td className="border-r border-black p-2 text-right">{formatMoney(getColTotal(3))}</td>
                  <td className="p-2 text-right">{formatMoney(getTotalPA())}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 text-sm leading-relaxed text-black">
            <p className="mb-2">In addition to the above fees:</p>
            <ol className="list-[lower-roman] pl-8 space-y-2">
              <li>
                All students will be required to pay attachment fees of <strong>Ksh. {formatMoney(institution?.attachment_fee || 8250)}</strong> during their last module.
              </li>
            </ol>
          </div>
        </div>


      </div>

      <style jsx="true">{`
        @media print {
          body * {
            visibility: hidden;
          }
          #root, #root * {
            visibility: visible;
          }
          header, nav, aside {
            display: none !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          /* This class scopes the print to just the document */
          .bg-white.border.shadow-sm.p-10.md\\:p-14 {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
            box-shadow: none;
            border: none;
          }
        }
      `}</style>
    </div>
  );
};

export default FeeStructure;
